import { Check, Clock, CreditCard, Package, Truck, Home, XCircle } from "lucide-react";

const STEPS = [
  { key: "pending", label: "En attente", icon: Clock },
  { key: "paid", label: "Payée", icon: CreditCard },
  { key: "preparing", label: "Préparation", icon: Package },
  { key: "shipped", label: "Expédiée", icon: Truck },
  { key: "delivered", label: "Livrée", icon: Home },
];

/**
 * Order progress stepper.
 *
 * Backend statuses: pending | paid | shipped | delivered | cancelled
 *
 * "preparing" is not a real backend status — we synthesize it visually:
 *   paid orders without `shipped_at` show "Préparation" highlighted.
 *
 * Cancelled orders short-circuit to a dedicated state.
 */
export default function OrderStepper({ status, paymentStatus }) {
  if (status === "cancelled") {
    return (
      <div
        className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B6B] border border-[#FF6B6B]/30 bg-[#FF6B6B]/10 px-3 py-2"
        data-testid="order-stepper-cancelled"
      >
        <XCircle size={14} /> Commande annulée
      </div>
    );
  }

  // Determine which step index is "current"
  let currentIdx = 0;
  if (status === "pending") currentIdx = paymentStatus === "paid" ? 1 : 0;
  else if (status === "paid") currentIdx = 2; // visually "preparing"
  else if (status === "shipped") currentIdx = 3;
  else if (status === "delivered") currentIdx = 4;

  return (
    <div
      className="mt-5"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={STEPS.length - 1}
      aria-valuenow={currentIdx}
      data-testid="order-stepper"
    >
      <div className="flex items-center justify-between">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = i < currentIdx;
          const active = i === currentIdx;
          const upcoming = i > currentIdx;

          const circleClass = done
            ? "bg-emerald-500 border-emerald-500 text-white"
            : active
            ? "bg-[#FFD700] border-[#FFD700] text-[#001F3F] ring-4 ring-[#FFD700]/20"
            : "bg-transparent border-white/25 text-white/40";

          const labelClass = done
            ? "text-emerald-300"
            : active
            ? "text-[#FFD700]"
            : "text-white/40";

          return (
            <div
              key={step.key}
              className="flex-1 flex flex-col items-center relative min-w-0"
              data-testid={`order-step-${step.key}`}
            >
              {/* Connector line to next step */}
              {i < STEPS.length - 1 && (
                <div
                  className={`absolute top-3.5 left-1/2 w-full h-[1.5px] ${
                    i < currentIdx ? "bg-emerald-500" : "bg-white/15"
                  }`}
                  aria-hidden
                />
              )}
              <div
                className={`relative z-10 w-7 h-7 border flex items-center justify-center transition-all ${circleClass}`}
              >
                {done ? <Check size={14} strokeWidth={3} /> : <Icon size={12} strokeWidth={2} />}
              </div>
              <div
                className={`mt-2 text-[9px] font-bold uppercase tracking-[0.18em] text-center leading-tight ${labelClass} ${
                  upcoming ? "hidden sm:block" : ""
                }`}
              >
                {step.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
